import {
    useState, useEffect, useCallback
} from 'react';
import {
    studioAgentService
} from 'shared/api';

/**
 * useSessions — BEXI recents state for one organization.
 * Owns the session LIST, the current session id, and pin state (localStorage,
 * per the spec). Message loading stays in the panel (it owns the transcript).
 */
export function useSessions(oragID) {
    const [sessions, setSessions] = useState([]);
    const [currentId, setCurrentId] = useState(null);
    const pinnedKey = `bexi:pinned:${oragID}`;

    const [pinned, setPinned] = useState(() => {
        try {
            return new Set(JSON.parse(localStorage.getItem(pinnedKey) || '[]'));
        } catch {
            return new Set();
        }
    });

    const refresh = useCallback(async () => {
        if (!oragID) return;
        try {
            const r = await studioAgentService.listSessions(oragID);
            if (r?.ok) setSessions(r.sessions || []);
        } catch { /* list is non-critical */ }
    }, [oragID]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const togglePin = (id) => setPinned((prev) => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        try { localStorage.setItem(pinnedKey, JSON.stringify([...n])); } catch { /* ignore */ }
        return n;
    });

    const create = async (title) => {
        const r = await studioAgentService.createSession(oragID, title);
        await refresh();
        return r?.session;
    };
    const rename = async (id, title) => {
        await studioAgentService.renameSession(oragID, id, title);
        await refresh();
    };
    const remove = async (id) => {
        await studioAgentService.deleteSession(oragID, id);
        await refresh();
    };

    // Pinned first, then newest-updated.
    const sorted = [...sessions].sort((a, b) => {
        const pa = pinned.has(a.id);
        const pb = pinned.has(b.id);
        if (pa !== pb) return pa ? -1 : 1;
        return a.updatedAt < b.updatedAt ? 1 : -1;
    });

    return {
        sessions: sorted,
        currentId,
        setCurrentId,
        pinned,
        togglePin,
        refresh,
        create,
        rename,
        remove,
    };
}
